import React, { cloneElement, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import usePosition from "./usePosition";
import "./Tooltip.scss";

// Fontaine M24 — Popover: a small floating panel anchored to a trigger (menus, filters, help, pickers).
// Controlled or uncontrolled; closes on outside click, Escape and Tab-out; optional focus trap for
// panels with several controls; returns focus to the trigger. Select, Menu and DatePicker share the engine.

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @param {ReactElement} trigger              ONE element; gets aria-haspopup / aria-expanded / aria-controls
 * @param {ReactNode|((api: {close: () => void}) => ReactNode)} children
 * @param {boolean} [open] / [onOpenChange]    controlled
 * @param {"bottom"|"top"|"left"|"right"} [side="bottom"]
 * @param {"start"|"center"|"end"} [align="start"]
 * @param {boolean} [trapFocus]               keep Tab inside the panel (forms, filter panels)
 * @param {boolean} [matchWidth]
 * @param {string} [label]                    accessible name of the panel (role=dialog)
 */
export default function Popover({ trigger, children, open: openProp, onOpenChange, defaultOpen = false, side = "bottom", align = "start", trapFocus = false, matchWidth = false, label, className = "" }) {
  const id = useId();
  const panelId = `ft-pop-${id.replace(/:/g, "")}`;
  const [inner, setInner] = useState(defaultOpen);
  const open = openProp ?? inner;
  const setOpen = (v) => { if (openProp === undefined) setInner(v); onOpenChange?.(v); };
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const { style } = usePosition(anchorRef, panelRef, { open, side, align, matchWidth });

  const close = () => { setOpen(false); anchorRef.current?.focus?.(); };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (anchorRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return; setOpen(false); };
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(); return; }
      if (e.key !== "Tab" || !panelRef.current) return;
      const nodes = [...panelRef.current.querySelectorAll(FOCUSABLE)];
      if (!nodes.length) { if (!trapFocus) setOpen(false); return; }
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (trapFocus) {
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      } else if (!e.shiftKey && document.activeElement === last) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    // move focus into the panel
    const t = setTimeout(() => { const first = panelRef.current?.querySelector(FOCUSABLE); (first || panelRef.current)?.focus?.(); }, 0);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); clearTimeout(t); };
  }, [open, trapFocus]); // eslint-disable-line react-hooks/exhaustive-deps

  const child = React.Children.only(trigger);
  const anchor = cloneElement(child, {
    ref: (el) => { anchorRef.current = el; const r = child.ref; if (typeof r === "function") r(el); else if (r) r.current = el; },
    "aria-haspopup": child.props["aria-haspopup"] || "dialog",
    "aria-expanded": open,
    "aria-controls": open ? panelId : undefined,
    onClick: (e) => { child.props.onClick?.(e); setOpen(!open); },
  });

  return (
    <>
      {anchor}
      {open && typeof document !== "undefined"
        ? createPortal(
            // eslint-disable-next-line no-restricted-syntax -- TODO(M02): runtime floating position
            <div role="dialog" aria-label={label} id={panelId} ref={panelRef} tabIndex={-1} className={`ft-pop ${className}`.trim()} style={style}>
              {typeof children === "function" ? children({ close }) : children}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
